import type {
  BbpsipBpsipPenyuluh,
  BbpsipBpsipWilayahKerja,
  BppsdmpKeahlian,
  BppsdmpPenyuluh,
  CityPenyuluh,
  DistrictPenyuluh,
  Penyuluh,
  PenyuluhSkill,
  ProvincePenyuluh
} from './types'

export interface PenyuluhSkillLine {
  sector: string
  skill: string
}

function asText(value: unknown) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function sectorName(item: PenyuluhSkill | BppsdmpKeahlian) {
  if (typeof item.subsektor === 'string') return asText(item.subsektor)
  if (item.subsektor && typeof item.subsektor === 'object') {
    return asText(item.subsektor.nama)
  }

  if ('nama_subsektor' in item) {
    return asText(item.nama_subsektor)
  }

  return asText(item.sector)
}

function skillName(item: PenyuluhSkill | BppsdmpKeahlian) {
  return (
    asText(item.keahlian) ||
    asText(item.skill) ||
    asText('nama' in item ? item.nama : null) ||
    asText(item.name)
  )
}

export function toPenyuluhSkillLines(
  skill: Penyuluh['skill'] | BppsdmpPenyuluh['keahlian'] | null | undefined
): PenyuluhSkillLine[] {
  if (!skill) return []

  if (typeof skill === 'string') {
    const value = skill.trim()
    if (!value) return []

    return value
      .split(/\r?\n|;/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const separator = line.indexOf(':')
        if (separator <= 0) {
          return { sector: '', skill: line }
        }

        return {
          sector: line.slice(0, separator).trim(),
          skill: line.slice(separator + 1).trim()
        }
      })
  }

  if (!Array.isArray(skill)) return []

  return skill
    .map(item => ({
      sector: sectorName(item),
      skill: skillName(item)
    }))
    .filter(item => item.sector || item.skill)
}

export function formatPenyuluhGender(gender?: string | null) {
  const value = asText(gender).toUpperCase().replaceAll('_', '-')

  if (value === 'L' || value === 'LAKI-LAKI' || value === 'LAKI LAKI') {
    return 'Laki-laki'
  }

  if (value === 'P' || value === 'PEREMPUAN') {
    return 'Perempuan'
  }

  return asText(gender) || '-'
}

export function formatPenyuluhAge(age?: string | number | null) {
  if (age === null || age === undefined || age === '') return null

  const numeric = typeof age === 'number' ? age : Number(age)
  if (!Number.isFinite(numeric) || numeric <= 0) return null

  return `${numeric} tahun`
}

function asKeahlianList(value: unknown): BppsdmpKeahlian[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is BppsdmpKeahlian =>
      typeof item === 'object' && item !== null
  )
}

function asWilayahKerjaList(value: unknown): BppsdmpPenyuluh['wilayah_kerja'] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is BppsdmpPenyuluh['wilayah_kerja'][number] =>
      typeof item === 'object' && item !== null
  )
}

function parseAge(...values: unknown[]): number | null {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value > 0 ? value : null
    }
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric > 0) return numeric
  }
  return null
}

function asBbpsipWilayahKerjaList(
  value: unknown
): BbpsipBpsipWilayahKerja[] {
  if (!Array.isArray(value)) return []

  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item !== null
    )
    .map(item => ({
      kode: asText(item.kode) || null,
      nama: asText(item.nama) || asText(item.name) || null,
      wilayah_kerja: Array.isArray(item.wilayah_kerja)
        ? asBbpsipWilayahKerjaList(item.wilayah_kerja)
        : undefined
    }))
    .filter(item => item.kode || item.nama)
}

/** Normalize BPPSDMP list item from API (Indonesian keys). */
export function toBppsdmpPenyuluh(raw: Record<string, unknown>): BppsdmpPenyuluh {
  return {
    id: asText(raw.id) || String(raw.id ?? ''),
    nama: asText(raw.nama) || asText(raw.name),
    nip: asText(raw.nip),
    jenis_kelamin:
      asText(raw.jenis_kelamin) || asText(raw.gender),
    usia: parseAge(raw.usia, raw.umur, raw.age),
    instansi: asText(raw.instansi) || asText(raw.institution) || null,
    jenis_penyuluh:
      asText(raw.jenis_penyuluh) || asText(raw.type) || null,
    jabatan: asText(raw.jabatan) || asText(raw.position) || null,
    pendidikan: asText(raw.pendidikan) || asText(raw.pddkn) || null,
    keahlian: asKeahlianList(raw.keahlian ?? raw.keahlihan ?? raw.skill),
    wilayah_kerja: asWilayahKerjaList(raw.wilayah_kerja),
    status: asText(raw.status) || '-'
  }
}

export function toBppsdmpPenyuluhList(
  data: unknown[] | null | undefined
): BppsdmpPenyuluh[] {
  if (!Array.isArray(data)) return []

  return data.map(item =>
    toBppsdmpPenyuluh(
      typeof item === 'object' && item !== null
        ? (item as Record<string, unknown>)
        : {}
    )
  )
}

/** Normalize BBPPMP & BRMP list item (`umur`, nested `wilayah_kerja`). */
export function toBbpsipBpsipPenyuluh(
  raw: Record<string, unknown>
): BbpsipBpsipPenyuluh {
  return {
    id: asText(raw.id) || String(raw.id ?? ''),
    nama: asText(raw.nama) || asText(raw.name),
    nip: asText(raw.nip),
    jenis_kelamin: asText(raw.jenis_kelamin) || asText(raw.gender),
    usia: parseAge(raw.umur, raw.usia, raw.age),
    instansi: asText(raw.instansi) || asText(raw.institution) || null,
    jenis_penyuluh: asText(raw.jenis_penyuluh) || asText(raw.type) || null,
    jabatan: asText(raw.jabatan) || asText(raw.position) || null,
    pendidikan: asText(raw.pendidikan) || asText(raw.pddkn) || null,
    keahlian: asKeahlianList(raw.keahlian ?? raw.keahlihan ?? raw.skill),
    wilayah_kerja: asBbpsipWilayahKerjaList(raw.wilayah_kerja),
    status: asText(raw.status) || '-'
  }
}

export function toBbpsipBpsipPenyuluhList(
  data: unknown[] | null | undefined
): BbpsipBpsipPenyuluh[] {
  if (!Array.isArray(data)) return []

  return data.map(item =>
    toBbpsipBpsipPenyuluh(
      typeof item === 'object' && item !== null
        ? (item as Record<string, unknown>)
        : {}
    )
  )
}

/** Normalize Provinsi list item (`umur`, `keahlihan` typo, nested wilker). */
export function toProvincePenyuluh(
  raw: Record<string, unknown>
): ProvincePenyuluh {
  return {
    id: asText(raw.id) || String(raw.id ?? ''),
    nama: asText(raw.nama) || asText(raw.name),
    nip: asText(raw.nip),
    jenis_kelamin: asText(raw.jenis_kelamin) || asText(raw.gender),
    usia: parseAge(raw.umur, raw.usia, raw.age),
    jenis_penyuluh: asText(raw.jenis_penyuluh) || asText(raw.type) || null,
    jabatan: asText(raw.jabatan) || asText(raw.position) || null,
    pendidikan: asText(raw.pendidikan) || asText(raw.pddkn) || null,
    keahlian: asKeahlianList(raw.keahlian ?? raw.keahlihan ?? raw.skill),
    wilayah_kerja: asBbpsipWilayahKerjaList(raw.wilayah_kerja),
    status: asText(raw.status) || '-'
  }
}

export function toProvincePenyuluhList(
  data: unknown[] | null | undefined
): ProvincePenyuluh[] {
  if (!Array.isArray(data)) return []

  return data.map(item =>
    toProvincePenyuluh(
      typeof item === 'object' && item !== null
        ? (item as Record<string, unknown>)
        : {}
    )
  )
}

/** Normalize Kabupaten/Kota list (same shape; wilayah_kerja up to 3 levels). */
export function toCityPenyuluhList(
  data: unknown[] | null | undefined
): CityPenyuluh[] {
  return toProvincePenyuluhList(data)
}

/** Normalize Kecamatan list (`instansi` = BPP; wilayah_kerja up to 4 levels). */
export function toDistrictPenyuluh(
  raw: Record<string, unknown>
): DistrictPenyuluh {
  return {
    id: asText(raw.id) || String(raw.id ?? ''),
    nama: asText(raw.nama) || asText(raw.name),
    nip: asText(raw.nip),
    jenis_kelamin: asText(raw.jenis_kelamin) || asText(raw.gender),
    usia: parseAge(raw.umur, raw.usia, raw.age),
    jenis_penyuluh: asText(raw.jenis_penyuluh) || asText(raw.type) || null,
    jabatan: asText(raw.jabatan) || asText(raw.position) || null,
    pendidikan: asText(raw.pendidikan) || asText(raw.pddkn) || null,
    keahlian: asKeahlianList(raw.keahlian ?? raw.keahlihan ?? raw.skill),
    instansi:
      asText(raw.instansi) ||
      asText(raw.bpp) ||
      asText(raw.institution) ||
      null,
    wilayah_kerja: asBbpsipWilayahKerjaList(raw.wilayah_kerja),
    status: asText(raw.status) || '-'
  }
}

export function toDistrictPenyuluhList(
  data: unknown[] | null | undefined
): DistrictPenyuluh[] {
  if (!Array.isArray(data)) return []

  return data.map(item =>
    toDistrictPenyuluh(
      typeof item === 'object' && item !== null
        ? (item as Record<string, unknown>)
        : {}
    )
  )
}

export function formatWilayahKerjaLines(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string[] {
  if (!items?.length) return []

  const lines: string[] = []

  for (const item of items) {
    const parent = asText(item.nama)
    const nested = item.wilayah_kerja ?? []

    if (nested.length > 0) {
      const childLines = formatWilayahKerjaLines(nested)

      if (childLines.length === 0) {
        if (parent) lines.push(parent)
        continue
      }

      for (const child of childLines) {
        lines.push(parent ? `${parent}: ${child}` : child)
      }
      continue
    }

    if (parent) lines.push(parent)
  }

  return lines
}

function collectUniqueNamesAtDepth(
  items: BbpsipBpsipWilayahKerja[] | null | undefined,
  targetDepth: number,
  currentDepth = 0
): string[] {
  if (!items?.length) return []

  const names = new Set<string>()

  for (const item of items) {
    const name = asText(item.nama)

    if (currentDepth === targetDepth) {
      if (name) names.add(name)
      continue
    }

    for (const nestedName of collectUniqueNamesAtDepth(
      item.wilayah_kerja,
      targetDepth,
      currentDepth + 1
    )) {
      names.add(nestedName)
    }
  }

  return [...names]
}

/** Unique province names from nested wilayah_kerja (level 0). */
export function formatDistrictProvinsi(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string {
  const names = collectUniqueNamesAtDepth(items, 0).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
  return names.length > 0 ? names.join(', ') : '-'
}

/** Unique city names from nested wilayah_kerja (level 1). */
export function formatDistrictKabupatenKota(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string {
  const names = collectUniqueNamesAtDepth(items, 1).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
  return names.length > 0 ? names.join(', ') : '-'
}

/** Unique kecamatan names from city-tab wilayah_kerja (level 2). */
export function getCityKecamatanNames(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string[] {
  return collectUniqueNamesAtDepth(items, 2).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
}

/** Unique kabupaten/kota names from nested wilayah_kerja (level 1). */
export function getKabupatenKotaNames(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string[] {
  return collectUniqueNamesAtDepth(items, 1).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
}

/**
 * Province-tab wilayah_kerja nests kabupaten at level 0 and provinsi at level 1.
 */
export function formatProvinceTabProvinsi(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string {
  const names = collectUniqueNamesAtDepth(items, 1).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
  return names.length > 0 ? names.join(', ') : '-'
}

export function getProvinceTabKabupatenNames(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string[] {
  return collectUniqueNamesAtDepth(items, 0).sort((a, b) =>
    a.localeCompare(b, 'id', { sensitivity: 'base' })
  )
}

/**
 * Kecamatan + desa from nested wilayah_kerja (levels 2–3).
 * Same kecamatan is grouped together.
 */
export function getDistrictWilayahKerjaGroups(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): Array<{ kecamatan: string; desas: string[] }> {
  if (!items?.length) return []

  const kecamatanMap = new Map<string, Set<string>>()

  const walk = (nodes: BbpsipBpsipWilayahKerja[], depth: number) => {
    for (const node of nodes) {
      const name = asText(node.nama)
      const nested = node.wilayah_kerja ?? []

      if (depth === 2) {
        if (!name) continue

        if (!kecamatanMap.has(name)) {
          kecamatanMap.set(name, new Set())
        }

        for (const desa of nested) {
          const desaName = asText(desa.nama)
          if (desaName) kecamatanMap.get(name)!.add(desaName)
        }
        continue
      }

      if (nested.length > 0) {
        walk(nested, depth + 1)
      }
    }
  }

  walk(items, 0)

  return [...kecamatanMap.entries()]
    .map(([kecamatan, desas]) => ({
      kecamatan,
      desas: [...desas].sort((a, b) =>
        a.localeCompare(b, 'id', { sensitivity: 'base' })
      )
    }))
    .sort((a, b) =>
      a.kecamatan.localeCompare(b.kecamatan, 'id', { sensitivity: 'base' })
    )
}

export function summarizeDistrictWilayahKerja(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): { kecamatanCount: number; desaCount: number } | null {
  const groups = getDistrictWilayahKerjaGroups(items)
  if (groups.length === 0) return null

  return {
    kecamatanCount: groups.length,
    desaCount: groups.reduce((total, group) => total + group.desas.length, 0)
  }
}

export function formatDistrictWilayahKerjaLines(
  items: BbpsipBpsipWilayahKerja[] | null | undefined
): string[] {
  return getDistrictWilayahKerjaGroups(items).map(({ kecamatan, desas }) => {
    if (desas.length === 0) return kecamatan
    return `${kecamatan}: ${desas.join(', ')}`
  })
}
